import { useEffect, useMemo, useRef } from "react";
import { Button } from "react-bootstrap";
import { FaCircle } from "react-icons/fa";
import MessageItem from "./MessageItem";

const DEFAULT_ICON = "https://password-super.s3.ap-south-1.amazonaws.com/icons/useryellow.png";

function formatTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function getFirstLetter(value) {
  const s = value ? String(value).trim() : "";
  return s ? s.charAt(0).toUpperCase() : "?";
}

function buildDisplayName({ isGroup, isMe, meName, meId, peerName, peerId, from, fromName, resolveUserName }) {
  if (isMe) return meName || meId;

  if (isGroup) {
    const direct = String(fromName ?? "").trim();
    if (direct) return direct;
    const resolved = resolveUserName?.(from);
    return resolved || from || "";
  }

  return peerName || peerId || from || "";
}

function getStatusClass(liveStatus, isGroup) {
  if (isGroup) return "text-success";
  if (liveStatus === "online") return "text-success";
  if (liveStatus === "away") return "text-warning";
  return "text-secondary small rounded-1";
}

const ChatMessages = ({
  onOpenThread,
  setSelectedProfile,
  channelName,
  messages = [],
  meId,
  meName,
  peerId,
  peerName,
  isTyping,
  chatToken,
  isGroup = false,
  groupAllowed = true,
  resolveUserName,
  groupTypingText = "",
}) => {
  const endRef = useRef(null);

  const uiMessages = useMemo(() => {
    return (messages || []).map((m) => {
      const isMe = m?.senderId === meId;
      const displayName = buildDisplayName({
        isGroup,
        isMe,
        meName,
        meId,
        peerName,
        peerId,
        from: m?.senderId,
        fromName: m?.fromName || m?.name,
        resolveUserName,
      });
      return {
        id: m?.messageId,
        user: displayName,
        avatar: getFirstLetter(displayName),
        time: formatTime(m?.createdAt),
        text: m?.message || "",
        attachments: m?.files || [],
        repliesCount: 0,
        isMe,
        status: m?.status || "sent",
        raw: m,
      };
    });
  }, [messages, meId, meName, peerId, peerName, isGroup, resolveUserName]);

  const iconSrc = channelName?.icon || DEFAULT_ICON;
  const title = channelName?.name;
  const isOwn = channelName?.own;
  const designation = channelName?.designationName;

  const liveStatusClass = getStatusClass(channelName?.liveStatus, isGroup);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [uiMessages.length, isTyping, groupTypingText]);

  return (
    <div className="chat-messages">
      <div className="hstack gap-2 mb-3">
        <img className="rounded-2" src={iconSrc} alt={title} style={{ height: 80 }} />
        <div>
          <div className="hstack gap-2">
            <h6 className="m-0 fw-bold section-title">
              {title} {isOwn && "(You)"}
            </h6>
            <div className={liveStatusClass}>
              <FaCircle style={{ fontSize: 8 }} />
            </div>
          </div>

          <p className="m-0 section-title">{designation}</p>

          {/* DM typing */}
          {!isGroup && isTyping && (
            <p className="m-0 section-title" style={{ fontSize: 12, opacity: 0.8 }}>
              {title || "User"} is typing...
            </p>
          )}

          {/* Group typing */}
          {isGroup && groupAllowed && groupTypingText && (
            <p className="m-0 section-title" style={{ fontSize: 12, opacity: 0.8 }}>
              {groupTypingText}
            </p>
          )}
        </div>
      </div>

      {!isGroup ? (
        <h6 className="fw-bold section-title">This conversation is just between @{title} and you.</h6>
      ) : !groupAllowed ? (
        <h6 className="fw-bold section-title">You are not assigned to this group.</h6>
      ) : (
        <h6 className="fw-bold section-title">This is a group conversation in #{title}.</h6>
      )}

      <Button
        className="p-1 px-3 rounded-1 hstack gap-2 justify-content-center"
        style={{ fontSize: 13 }}
        variant="outline-primary"
        onClick={() => setSelectedProfile?.(channelName)}
      >
        View Profile
      </Button>

      <div className="date-divider">Today</div>

      {uiMessages.map((msg, index) => {
        const prevMsg = uiMessages[index - 1];
        const isGrouped = Boolean(prevMsg && prevMsg.user === msg.user);

        return (
          <MessageItem
            key={msg.id || index}
            message={msg}
            isGrouped={isGrouped}
            onReply={onOpenThread}
            setSelectedProfile={setSelectedProfile}
            chatToken={chatToken}
          />
        );
      })}

      <div ref={endRef} />
    </div>
  );
};

export default ChatMessages;

